// src/components/Navbar.js
import React, { useState, useEffect } from "react";
import { Bell, User, Search, LogOut } from "lucide-react";
import { jwtDecode } from "jwt-decode";
import { Link } from "react-router-dom";
// ✅ Import the function to fetch alerts
import { fetchTriggeredAlerts } from "../utils/alertApi";

const Navbar = ({ onLogout }) => {
    const [userEmail, setUserEmail] = useState("User");
    const [unreadAlertCount, setUnreadAlertCount] = useState(0); // ✅ State for alert count

    // --- Fetch User Email (Keep as is) ---
    useEffect(() => {
        const token = localStorage.getItem('piwatch_token');
        // ... (rest of token decoding logic remains the same) ...
         if (token) {
            try {
                const decodedToken = jwtDecode(token);
                if (decodedToken && decodedToken.sub) {
                    setUserEmail(decodedToken.sub);
                }
            } catch (error) {
                console.error("Error decoding JWT token:", error);
                setUserEmail("User");
            }
        } else {
            setUserEmail("Guest"); // Should ideally not happen if Navbar only shown when logged in
        }
    }, []);

    // --- ✅ Fetch Unread Alert Count Periodically ---
    useEffect(() => {
        let isMounted = true;
        const fetchAlertCount = async () => {
            // Fetch only unacknowledged alerts
            const unreadAlerts = await fetchTriggeredAlerts(false); // acknowledged = false
            if (isMounted) {
                // Set the count based on the length of the returned array
                setUnreadAlertCount(Array.isArray(unreadAlerts) ? unreadAlerts.length : 0);
            }
        };

        fetchAlertCount(); // Fetch immediately on mount
        const interval = setInterval(fetchAlertCount, 15000); // Refresh count every 15 seconds

        return () => {
            isMounted = false;
            clearInterval(interval); // Cleanup on unmount
        };
    }, []); // Empty dependency array, runs once on mount and sets up interval

    return (
        <nav className="sticky top-0 z-40 flex items-center justify-between bg-gray-900/60 backdrop-blur-md px-6 py-4 rounded-xl shadow-[0_0_15px_rgba(0,255,255,0.2)] border-b border-gray-800 mb-6">
            {/* Left section - Search */}
            <div className="flex items-center gap-3">
                 <Search className="text-neonBlue" size={20} />
                 <input
                     type="text"
                     placeholder="Search..."
                     className="bg-transparent text-sm text-gray-300 outline-none placeholder-gray-500 w-40 sm:w-56 transition-all"
                 />
            </div>

            {/* Right section - Icons & User */}
            <div className="flex items-center gap-5">

                {/* ✅ Bell Icon with Badge */}
                <Link to="/alerts" className="relative group" title="View Alerts">
                    <Bell className="text-neonPink cursor-pointer group-hover:scale-110 transition-transform" size={22} />
                    {unreadAlertCount > 0 && (
                        <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                            {unreadAlertCount > 9 ? '9+' : unreadAlertCount} {/* Show count or 9+ */}
                        </span>
                    )}
                </Link>

                {/* User Info Link */}
                <Link to="/profile" className="flex items-center gap-2 cursor-pointer group" title="View Profile">
                    {/* ... (User icon and email remain the same) ... */}
                    <User className="text-neonBlue group-hover:text-white transition-colors" size={24} />
                    <span className="hidden sm:inline text-sm text-gray-300 font-medium group-hover:text-white transition-colors">
                        {userEmail}
                    </span>
                </Link>

                {/* Logout Button */}
                <button onClick={onLogout} title="Logout" className="text-gray-400 hover:text-red-500 transition-colors">
                    {/* ... (Logout icon remains the same) ... */}
                     <LogOut size={22} />
                </button>
            </div>
        </nav>
    );
};

export default Navbar; // Ensure export default is present